import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Clock, ShieldCheck, AlertTriangle, FileText, Eye, CheckCircle2, XCircle, CalendarPlus,
  ListPlus, Inbox, Search, Mail, Phone, Building2, BookOpen, MoreHorizontal, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const ACCESS_BADGE = {
  limited_access_active: { color: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700", label: "Limited Access" },
  limited_access_expired: { color: "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700", label: "Expired" },
  pending_payment_verification: { color: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-700", label: "Pending Verification" },
  payment_rejected: { color: "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-700", label: "Payment Rejected" },
  pending_approval: { color: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600", label: "Pending Approval" },
};

const PAYMENT_BADGE = {
  not_paid: { color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200", label: "Not Paid" },
  pending_verification: { color: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200", label: "Pending Verification" },
  verified: { color: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200", label: "Verified" },
  rejected: { color: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200", label: "Rejected" },
  confirmed_manually: { color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200", label: "Confirmed Manually" },
};

function dateOnly(s) { return (s || "").slice(0, 10); }

function fmtCount(used, limit) {
  if (!limit || limit <= 0) return `${used || 0}`;
  return `${used || 0} / ${limit}`;
}

export default function TemporaryAccessStudentsPage() {
  const { user } = useAuth();
  const canView = hasPerm(user, "temporary_access_students", "view");
  const canViewReceipt = hasPerm(user, "temporary_access_students", "view_receipt");
  const canGrant = hasPerm(user, "temporary_access_students", "grant_permanent_access");
  const canConfirmManually = hasPerm(user, "temporary_access_students", "confirm_payment_manually");
  const canReject = hasPerm(user, "temporary_access_students", "reject_payment");
  const canExtend = hasPerm(user, "temporary_access_students", "extend_access");
  const canAddQuestions = hasPerm(user, "temporary_access_students", "reset_question_limit");

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    course_id: "",
    access_status: "",
    payment_status: "",
    receipt: "",
    limit_reached: "",
  });
  const [courses, setCourses] = useState([]);

  // Dialog states
  const [viewing, setViewing] = useState(null); // {student, payment, audit_logs}
  const [receipt, setReceipt] = useState(null); // payment doc
  const [grant, setGrant] = useState({ open: false, student: null, reason: "" });
  const [manual, setManual] = useState({ open: false, student: null, ref: "", notes: "" });
  const [reject, setReject] = useState({ open: false, student: null, reason: "" });
  const [extend, setExtend] = useState({ open: false, student: null, days: 3, reason: "" });
  const [addQ, setAddQ] = useState({ open: false, student: null, additional: 10, reason: "" });
  const [busy, setBusy] = useState(false);

  const [nextCursor, setNextCursor] = useState(null);
  const tempReqId = useRef(0);
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => { const t = setTimeout(() => setDebouncedFilters(filters), 350); return () => clearTimeout(t); }, [filters]);
  const fetchPage = useCallback(async (cursor) => {
    const id = ++tempReqId.current;
    const params = { cursor, limit: 50 };
    Object.entries(debouncedFilters).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get("/admin/temporary-access-students", { params });
    if (id !== tempReqId.current) throw new Error("stale");
    setCounts(r.data?.counts || {});
    setNextCursor(r.data?.next_cursor ?? null);
    return r.data?.students || [];
  }, [debouncedFilters]);
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try { setRows(await fetchPage(0)); } catch (e) {
      if (e?.message === "stale") return;
      toast.error(e?.response?.data?.detail || "Could not load students");
    } finally { setLoading(false); }
  }, [fetchPage, canView]);
  const loadMore = async () => {
    if (nextCursor == null) return;
    setLoading(true);
    try {
      const more = await fetchPage(nextCursor);
      setRows((prev) => { const seen = new Set(prev.map((x) => x.row_key)); return [...prev, ...more.filter((x) => !seen.has(x.row_key))]; });
    } catch (e) { if (e?.message !== "stale") toast.error(e?.response?.data?.detail || "Could not load more"); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data || [])).catch(() => {});
  }, []);

  const openDetail = async (student) => {
    setViewing({ student: { ...student }, payment: null, audit_logs: [], loading: true });
    try {
      const r = await api.get(`/admin/temporary-access-students/${student.id}`, { params: { course_id: student.course_id } });
      setViewing({ ...r.data, loading: false });
    } catch (e) {
      toast.error("Could not load student details");
      setViewing(null);
    }
  };

  const openReceipt = async (student) => {
    try {
      const r = await api.get(`/admin/temporary-access-students/${student.id}/receipt`, { params: { payment_id: student.current_payment_id } });
      setReceipt({ student, payment: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Receipt not available");
    }
  };

  const doGrant = async () => {
    if (!grant.reason || grant.reason.trim().length < 3) {
      toast.error("Reason is required (min 3 chars)"); return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/temporary-access-students/${grant.student.id}/grant-permanent-access`, {
        reason: grant.reason.trim(),
        course_id: grant.student.course_id,
      });
      toast.success(`Permanent access granted for ${grant.student.course_name}.`);
      setGrant({ open: false, student: null, reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doManual = async () => {
    if (!manual.notes || manual.notes.trim().length < 3) {
      toast.error("Admin notes / confirmation reason is required"); return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/temporary-access-students/${manual.student.id}/manual-confirm-payment`, {
        payment_reference_number: manual.ref || "",
        admin_notes: manual.notes.trim(),
        course_id: manual.student.course_id,
      });
      toast.success(`Payment confirmed — ${manual.student.course_name} unlocked.`);
      setManual({ open: false, student: null, ref: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!reject.reason || reject.reason.trim().length < 3) {
      toast.error("Rejection reason is required"); return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/temporary-access-students/${reject.student.id}/reject-payment`, {
        reason: reject.reason.trim(),
      });
      toast.success("Payment has been rejected.");
      setReject({ open: false, student: null, reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doExtend = async () => {
    const d = parseInt(extend.days, 10);
    if (!d || d <= 0) { toast.error("Enter days (positive integer)"); return; }
    if (!extend.reason || extend.reason.trim().length < 3) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/temporary-access-students/${extend.student.id}/extend-access`, {
        additional_days: d,
        reason: extend.reason.trim(),
        course_id: extend.student.course_id,
      });
      toast.success("Temporary access has been extended successfully.");
      setExtend({ open: false, student: null, days: 3, reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doAddQ = async () => {
    const n = parseInt(addQ.additional, 10);
    if (!n || n <= 0) { toast.error("Enter additional questions (positive integer)"); return; }
    if (!addQ.reason || addQ.reason.trim().length < 3) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/temporary-access-students/${addQ.student.id}/add-question-allowance`, {
        additional_questions: n,
        reason: addQ.reason.trim(),
      });
      toast.success("Question allowance has been updated successfully.");
      setAddQ({ open: false, student: null, additional: 10, reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const headerCounts = useMemo(() => ([
    { key: "limited_access_active", label: "Limited Active", value: counts.limited_access_active || 0, tone: "blue" },
    { key: "limited_access_expired", label: "Expired", value: counts.limited_access_expired || 0, tone: "rose" },
    { key: "pending_payment_verification", label: "Pending Verification", value: counts.pending_payment_verification || 0, tone: "amber" },
    { key: "payment_rejected", label: "Payment Rejected", value: counts.payment_rejected || 0, tone: "orange" },
  ]), [counts]);

  if (!canView) {
    return (
      <Card className="p-10 text-center">
        <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <div className="text-sm text-slate-500">You don't have permission to view this module.</div>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="temp-access-page">
      {/* Header */}
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" /> Temporary Access Students
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 text-slate-900 dark:text-slate-50">
          Manage temporary / unpaid students
        </h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">
          One row per enrolled course that isn't paid yet. Verify payments, grant permanent access, or extend
          trials per course. Students drop off once every course is unlocked.
        </p>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {headerCounts.map((c) => (
          <Card key={c.key} className={`p-4 border border-${c.tone}-200 dark:border-${c.tone}-800/50`} data-testid={`temp-stat-${c.key}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{c.label}</div>
            <div className={`text-2xl font-display font-bold mt-1 text-${c.tone}-600 dark:text-${c.tone}-300`}>{c.value}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-4 border-slate-200 dark:border-slate-700">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-xs">Search name or email</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Juan…"
                className="pl-9"
                data-testid="temp-filter-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Course</Label>
            <Select value={filters.course_id || "all"} onValueChange={(v) => setFilters({ ...filters, course_id: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="temp-filter-course"><SelectValue placeholder="All courses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All courses</SelectItem>
                {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.course_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Access Status</Label>
            <Select value={filters.access_status || "all"} onValueChange={(v) => setFilters({ ...filters, access_status: v === "all" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="temp-filter-access"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any access status</SelectItem>
                <SelectItem value="limited_access_active">Limited Active</SelectItem>
                <SelectItem value="limited_access_expired">Expired</SelectItem>
                <SelectItem value="pending_payment_verification">Pending Verification</SelectItem>
                <SelectItem value="payment_rejected">Payment Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Receipt</Label>
            <Select value={filters.receipt || "any"} onValueChange={(v) => setFilters({ ...filters, receipt: v === "any" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="temp-filter-receipt"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="yes">Uploaded</SelectItem>
                <SelectItem value="no">Not uploaded</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Question limit</Label>
            <Select value={filters.limit_reached || "any"} onValueChange={(v) => setFilters({ ...filters, limit_reached: v === "any" ? "" : v })}>
              <SelectTrigger className="mt-1" data-testid="temp-filter-limit"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="yes">Limit reached</SelectItem>
                <SelectItem value="no">Within quota</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-end mt-3 gap-2">
          <Button variant="outline" size="sm" onClick={() => setFilters({ search: "", course_id: "", access_status: "", payment_status: "", receipt: "", limit_reached: "" })} data-testid="temp-clear-filters">
            Clear
          </Button>
          <Button size="sm" onClick={load} disabled={loading} data-testid="temp-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </Card>

      {/* Table / Cards */}
      {rows.length === 0 ? (
        <Card className="p-12 text-center" data-testid="temp-empty">
          <Inbox className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No temporary-access students match your filters.</div>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="p-0 border-slate-200 dark:border-slate-700 hidden lg:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/50 text-left">
                  <tr className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Course</th>
                    <th className="px-4 py-3">Access</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3">Days Left</th>
                    <th className="px-4 py-3">Questions</th>
                    <th className="px-4 py-3">Receipt</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {rows.map((s) => {
                    const access = ACCESS_BADGE[s.access_status] || ACCESS_BADGE.pending_approval;
                    const pay = PAYMENT_BADGE[s.payment_status] || PAYMENT_BADGE.not_paid;
                    return (
                      <tr key={s.row_key} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`temp-row-${s.row_key}`}>
                        <td className="px-4 py-3">
                          <div className="font-display font-bold text-slate-900 dark:text-slate-50">{s.full_name}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</div>
                          {s.contact_number && <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1"><Phone className="w-3 h-3" /> {s.contact_number}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                          <div className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-slate-400 shrink-0" /> {s.course_name || "—"}</div>
                          {s.is_primary && <Badge variant="outline" className="mt-1 text-[10px] bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800" data-testid={`temp-primary-${s.row_key}`}>Primary</Badge>}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={access.color}>{access.label}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={pay.color}>{pay.label}</Badge>
                          {s.latest_payment_rejection_reason && (
                            <div className="text-[11px] text-rose-700 mt-1">"{s.latest_payment_rejection_reason}"</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                          {s.access_status === "limited_access_expired" ? (
                            <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800">Expired</Badge>
                          ) : `${s.days_remaining || 0}d`}
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                          <span className={s.is_question_limit_reached ? "text-rose-600 dark:text-rose-300 font-semibold" : ""}>
                            {fmtCount(s.questions_used, s.question_limit)}
                          </span>
                          {s.is_question_limit_reached && (
                            <Badge variant="outline" className="ml-2 bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-700">Limit Reached</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {s.receipt_uploaded ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800">Uploaded</Badge>
                          ) : (
                            <span className="text-xs text-slate-400">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <RowActions
                            student={s}
                            canViewReceipt={canViewReceipt && s.receipt_uploaded}
                            canGrant={canGrant}
                            canConfirmManually={canConfirmManually}
                            canReject={canReject && s.receipt_uploaded && s.is_current_payment}
                            canExtend={canExtend}
                            canAddQuestions={canAddQuestions && s.is_primary}
                            onView={() => openDetail(s)}
                            onViewReceipt={() => openReceipt(s)}
                            onGrant={() => setGrant({ open: true, student: s, reason: "" })}
                            onConfirm={() => setManual({ open: true, student: s, ref: s.latest_payment_reference_number || "", notes: "" })}
                            onReject={() => setReject({ open: true, student: s, reason: "" })}
                            onExtend={() => setExtend({ open: true, student: s, days: 3, reason: "" })}
                            onAddQ={() => setAddQ({ open: true, student: s, additional: 10, reason: "" })}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 lg:hidden">
            {rows.map((s) => {
              const access = ACCESS_BADGE[s.access_status] || ACCESS_BADGE.pending_approval;
              const pay = PAYMENT_BADGE[s.payment_status] || PAYMENT_BADGE.not_paid;
              return (
                <Card key={s.row_key} className="p-4 border-slate-200 dark:border-slate-700" data-testid={`temp-card-${s.row_key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-slate-900 dark:text-slate-50 truncate">{s.full_name}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{s.email}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1"><BookOpen className="w-3 h-3 shrink-0" /> {s.course_name || "—"}{s.is_primary && <span className="text-blue-600 font-semibold">· Primary</span>}</div>
                    </div>
                    <RowActions
                      student={s}
                      canViewReceipt={canViewReceipt && s.receipt_uploaded}
                      canGrant={canGrant}
                      canConfirmManually={canConfirmManually}
                      canReject={canReject && s.receipt_uploaded && s.is_current_payment}
                      canExtend={canExtend}
                      canAddQuestions={canAddQuestions && s.is_primary}
                      onView={() => openDetail(s)}
                      onViewReceipt={() => openReceipt(s)}
                      onGrant={() => setGrant({ open: true, student: s, reason: "" })}
                      onConfirm={() => setManual({ open: true, student: s, ref: s.latest_payment_reference_number || "", notes: "" })}
                      onReject={() => setReject({ open: true, student: s, reason: "" })}
                      onExtend={() => setExtend({ open: true, student: s, days: 3, reason: "" })}
                      onAddQ={() => setAddQ({ open: true, student: s, additional: 10, reason: "" })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <div>
                      <div className="text-slate-400 uppercase tracking-wider">Access</div>
                      <Badge variant="outline" className={`mt-1 ${access.color}`}>{access.label}</Badge>
                    </div>
                    <div>
                      <div className="text-slate-400 uppercase tracking-wider">Payment</div>
                      <Badge variant="outline" className={`mt-1 ${pay.color}`}>{pay.label}</Badge>
                    </div>
                    <div>
                      <div className="text-slate-400 uppercase tracking-wider">Days Left</div>
                      <div className="font-semibold text-slate-700 dark:text-slate-100">
                        {s.access_status === "limited_access_expired" ? "Expired" : `${s.days_remaining || 0}d`}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-400 uppercase tracking-wider">Questions</div>
                      <div className={`font-semibold ${s.is_question_limit_reached ? "text-rose-600 dark:text-rose-300" : "text-slate-700 dark:text-slate-100"}`}>
                        {fmtCount(s.questions_used, s.question_limit)}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600 dark:text-slate-300" data-testid="temp-pager">
          <span data-testid="temp-pager-summary">Showing {rows.length.toLocaleString()} course row{rows.length === 1 ? "" : "s"}</span>
          {nextCursor != null ? (
            <Button variant="outline" onClick={loadMore} disabled={loading} data-testid="temp-load-more">
              {loading ? "Loading…" : "Load more"}
            </Button>
          ) : (
            <span className="text-xs text-slate-400" data-testid="temp-pager-end">End of list</span>
          )}
        </div>
      )}

      {/* View Detail Dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="temp-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-blue-600" /> Student Details</DialogTitle>
          </DialogHeader>
          {viewing?.loading && <div className="text-center text-sm text-slate-500 py-8">Loading…</div>}
          {viewing && !viewing.loading && viewing.student && (
            <div className="space-y-4">
              <DetailGrid s={viewing.student} payment={viewing.payment} />
              {viewing.courses?.length > 1 && <CourseAccessList courses={viewing.courses} current={viewing.student.course_id} />}
              {viewing.payment && (
                <Card className="p-4 bg-slate-50 dark:bg-slate-800/50">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Payment Record</div>
                  <div className="text-sm">
                    <div><span className="text-slate-500">Status:</span> {viewing.payment.payment_status}</div>
                    {viewing.payment.payment_reference_number && (
                      <div><span className="text-slate-500">Reference:</span> <span className="font-mono">{viewing.payment.payment_reference_number}</span></div>
                    )}
                    <div><span className="text-slate-500">Submitted:</span> {(viewing.payment.submitted_at || "").slice(0, 19).replace("T", " ")}</div>
                    {viewing.payment.rejection_reason && <div className="text-rose-700">Rejection: {viewing.payment.rejection_reason}</div>}
                    {viewing.payment.proof_of_payment_data_url && (
                      <div className="mt-2">
                        {viewing.payment.proof_of_payment_mime === "application/pdf" ? (
                          <iframe src={viewing.payment.proof_of_payment_data_url} title="receipt" className="w-full h-72 border rounded bg-white" />
                        ) : (
                          <img src={viewing.payment.proof_of_payment_data_url} alt="receipt" className="max-w-full max-h-72 border rounded mx-auto bg-white" />
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              )}
              {viewing.audit_logs?.length > 0 && (
                <Card className="p-4 bg-slate-50 dark:bg-slate-800/50">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Admin Audit Log</div>
                  <ul className="space-y-2 text-xs">
                    {viewing.audit_logs.map((log) => (
                      <li key={log.id} className="border-l-2 border-blue-300 pl-2">
                        <div className="font-semibold text-slate-700 dark:text-slate-100">{log.action_type}</div>
                        <div className="text-slate-500">by {log.admin_name} · {(log.created_at || "").slice(0, 19).replace("T", " ")}</div>
                        {log.reason && <div className="italic text-slate-600 dark:text-slate-300">"{log.reason}"</div>}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
              <div className="flex flex-wrap gap-2 justify-end">
                {canGrant && !viewing.student.is_full && (
                  <Button onClick={() => { setGrant({ open: true, student: viewing.student, reason: "" }); setViewing(null); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="detail-grant-btn">
                    <ShieldCheck className="w-4 h-4 mr-1" /> Grant Permanent Access
                  </Button>
                )}
                {canConfirmManually && !viewing.student.is_full && (
                  <Button onClick={() => { setManual({ open: true, student: viewing.student, ref: viewing.student.latest_payment_reference_number || "", notes: "" }); setViewing(null); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="detail-manual-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm Payment Manually
                  </Button>
                )}
                {canExtend && !viewing.student.is_full && (
                  <Button variant="outline" onClick={() => { setExtend({ open: true, student: viewing.student, days: 3, reason: "" }); setViewing(null); }} data-testid="detail-extend-btn">
                    <CalendarPlus className="w-4 h-4 mr-1" /> Extend Access
                  </Button>
                )}
                {canAddQuestions && viewing.student.is_primary && (
                  <Button variant="outline" onClick={() => { setAddQ({ open: true, student: viewing.student, additional: 10, reason: "" }); setViewing(null); }} data-testid="detail-addq-btn">
                    <ListPlus className="w-4 h-4 mr-1" /> Reset Question Limit
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setViewing(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="max-w-2xl" data-testid="temp-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Payment Receipt</DialogTitle>
            <DialogDescription>{receipt?.student?.full_name}</DialogDescription>
          </DialogHeader>
          {receipt?.payment && (
            <div className="space-y-3">
              {receipt.payment.payment_reference_number && (
                <div className="text-sm"><span className="text-slate-500">Reference: </span><span className="font-mono">{receipt.payment.payment_reference_number}</span></div>
              )}
              <div className="text-xs text-slate-500">Uploaded: {(receipt.payment.submitted_at || "").slice(0, 19).replace("T", " ")}</div>
              {receipt.payment.proof_of_payment_data_url ? (
                receipt.payment.proof_of_payment_mime === "application/pdf" ? (
                  <iframe src={receipt.payment.proof_of_payment_data_url} title="receipt" className="w-full h-[480px] border rounded bg-white" />
                ) : (
                  <img src={receipt.payment.proof_of_payment_data_url} alt="Receipt" className="max-w-full max-h-[480px] border rounded mx-auto bg-white" />
                )
              ) : (
                <div className="text-sm text-slate-500 italic">No file uploaded — reference number only.</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceipt(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Permanent Access Dialog */}
      <Dialog open={grant.open} onOpenChange={(o) => !o && setGrant({ open: false, student: null, reason: "" })}>
        <DialogContent data-testid="temp-grant-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700"><ShieldCheck className="w-5 h-5" /> Grant Permanent Access</DialogTitle>
            <DialogDescription>
              Grant permanent full access to <b>{grant.student?.course_name}</b> for <b>{grant.student?.full_name}</b>?
              Only this course is unlocked — the student's other courses keep their own status.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason for granting permanent access <span className="text-rose-500">*</span></Label>
            <Textarea
              value={grant.reason}
              onChange={(e) => setGrant({ ...grant, reason: e.target.value.slice(0, 500) })}
              rows={3}
              placeholder="e.g. Bank transfer verified outside PayMongo / Scholarship granted / Promotional free access"
              data-testid="temp-grant-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGrant({ open: false, student: null, reason: "" })}>Cancel</Button>
            <Button onClick={doGrant} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="temp-grant-confirm">
              <ShieldCheck className="w-4 h-4 mr-1" /> Confirm Grant Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Confirm Payment Dialog */}
      <Dialog open={manual.open} onOpenChange={(o) => !o && setManual({ open: false, student: null, ref: "", notes: "" })}>
        <DialogContent data-testid="temp-manual-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700"><CheckCircle2 className="w-5 h-5" /> Confirm Payment Manually</DialogTitle>
            <DialogDescription>
              For <b>{manual.student?.full_name}</b> — <b>{manual.student?.course_name}</b>. Only this course is unlocked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment reference number <span className="text-slate-400 text-xs">(optional if already uploaded)</span></Label>
              <Input value={manual.ref} onChange={(e) => setManual({ ...manual, ref: e.target.value })} placeholder="e.g. GCASH-12345" data-testid="temp-manual-ref" />
            </div>
            <div>
              <Label>Admin notes / confirmation reason <span className="text-rose-500">*</span></Label>
              <Textarea
                value={manual.notes}
                onChange={(e) => setManual({ ...manual, notes: e.target.value.slice(0, 500) })}
                rows={3}
                placeholder="e.g. Receipt verified manually with finance team / Bank wire received"
                data-testid="temp-manual-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManual({ open: false, student: null, ref: "", notes: "" })}>Cancel</Button>
            <Button onClick={doManual} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="temp-manual-confirm">
              Confirm Payment &amp; Grant Full Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Payment Dialog */}
      <Dialog open={reject.open} onOpenChange={(o) => !o && setReject({ open: false, student: null, reason: "" })}>
        <DialogContent data-testid="temp-reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5" /> Reject Payment</DialogTitle>
            <DialogDescription>
              Reject <b>{reject.student?.full_name}</b>'s payment proof. They will be asked to upload a new receipt.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Rejection reason <span className="text-rose-500">*</span></Label>
            <Textarea
              value={reject.reason}
              onChange={(e) => setReject({ ...reject, reason: e.target.value.slice(0, 500) })}
              rows={3}
              placeholder="e.g. Reference number doesn't match transactions / Blurry receipt image"
              data-testid="temp-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReject({ open: false, student: null, reason: "" })}>Cancel</Button>
            <Button onClick={doReject} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="temp-reject-confirm">
              <XCircle className="w-4 h-4 mr-1" /> Reject Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Access Dialog */}
      <Dialog open={extend.open} onOpenChange={(o) => !o && setExtend({ open: false, student: null, days: 3, reason: "" })}>
        <DialogContent data-testid="temp-extend-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700"><CalendarPlus className="w-5 h-5" /> Extend Temporary Access</DialogTitle>
            <DialogDescription>
              Add more limited-access days for <b>{extend.student?.full_name}</b> on <b>{extend.student?.course_name}</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Additional days <span className="text-rose-500">*</span></Label>
              <Input type="number" min={1} max={180} value={extend.days} onChange={(e) => setExtend({ ...extend, days: e.target.value })} data-testid="temp-extend-days" />
            </div>
            <div>
              <Label>Reason <span className="text-rose-500">*</span></Label>
              <Textarea value={extend.reason} onChange={(e) => setExtend({ ...extend, reason: e.target.value.slice(0, 500) })} rows={3} placeholder="e.g. Student requested extension while finalizing payment" data-testid="temp-extend-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExtend({ open: false, student: null, days: 3, reason: "" })}>Cancel</Button>
            <Button onClick={doExtend} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="temp-extend-confirm">
              <CalendarPlus className="w-4 h-4 mr-1" /> Extend Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Question Allowance Dialog */}
      <Dialog open={addQ.open} onOpenChange={(o) => !o && setAddQ({ open: false, student: null, additional: 10, reason: "" })}>
        <DialogContent data-testid="temp-addq-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700"><ListPlus className="w-5 h-5" /> Add Question Allowance</DialogTitle>
            <DialogDescription>
              Grant <b>{addQ.student?.full_name}</b> more limited-access questions. Student remains on limited access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Additional questions allowed <span className="text-rose-500">*</span></Label>
              <Input type="number" min={1} value={addQ.additional} onChange={(e) => setAddQ({ ...addQ, additional: e.target.value })} data-testid="temp-addq-num" />
            </div>
            <div>
              <Label>Reason <span className="text-rose-500">*</span></Label>
              <Textarea value={addQ.reason} onChange={(e) => setAddQ({ ...addQ, reason: e.target.value.slice(0, 500) })} rows={3} placeholder="e.g. Student finishing pre-payment review" data-testid="temp-addq-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddQ({ open: false, student: null, additional: 10, reason: "" })}>Cancel</Button>
            <Button onClick={doAddQ} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="temp-addq-confirm">
              <ListPlus className="w-4 h-4 mr-1" /> Update Allowance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RowActions({
  student, canViewReceipt, canGrant, canConfirmManually, canReject, canExtend, canAddQuestions,
  onView, onViewReceipt, onGrant, onConfirm, onReject, onExtend, onAddQ,
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`temp-actions-${student.row_key}`}>
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={onView} data-testid={`temp-act-view-${student.row_key}`}>
          <Eye className="w-4 h-4 mr-2" /> View Details
        </DropdownMenuItem>
        {canViewReceipt && (
          <DropdownMenuItem onClick={onViewReceipt} data-testid={`temp-act-receipt-${student.row_key}`}>
            <FileText className="w-4 h-4 mr-2" /> View Receipt
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {canGrant && (
          <DropdownMenuItem onClick={onGrant} className="text-emerald-700 dark:text-emerald-300" data-testid={`temp-act-grant-${student.row_key}`}>
            <ShieldCheck className="w-4 h-4 mr-2" /> Grant Permanent Access
          </DropdownMenuItem>
        )}
        {canConfirmManually && (
          <DropdownMenuItem onClick={onConfirm} className="text-blue-700 dark:text-blue-300" data-testid={`temp-act-manual-${student.row_key}`}>
            <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm Payment Manually
          </DropdownMenuItem>
        )}
        {canReject && (
          <DropdownMenuItem onClick={onReject} className="text-rose-700 dark:text-rose-300" data-testid={`temp-act-reject-${student.row_key}`}>
            <XCircle className="w-4 h-4 mr-2" /> Reject Payment
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {canExtend && (
          <DropdownMenuItem onClick={onExtend} data-testid={`temp-act-extend-${student.row_key}`}>
            <CalendarPlus className="w-4 h-4 mr-2" /> Extend Access
          </DropdownMenuItem>
        )}
        {canAddQuestions && (
          <DropdownMenuItem onClick={onAddQ} data-testid={`temp-act-addq-${student.row_key}`}>
            <ListPlus className="w-4 h-4 mr-2" /> Reset Question Limit
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DetailGrid({ s, payment }) {
  const access = ACCESS_BADGE[s.access_status] || ACCESS_BADGE.pending_approval;
  const pay = PAYMENT_BADGE[s.payment_status] || PAYMENT_BADGE.not_paid;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
      <Field label="Full Name" value={s.full_name} />
      <Field label="Email" value={s.email} icon={<Mail className="w-3.5 h-3.5" />} />
      <Field label="Contact Number" value={s.contact_number || "—"} icon={<Phone className="w-3.5 h-3.5" />} />
      <Field label="School" value={s.school || "—"} icon={<Building2 className="w-3.5 h-3.5" />} />
      <Field label={s.is_primary ? "Course (Primary)" : "Course"} value={s.course_name || "—"} icon={<BookOpen className="w-3.5 h-3.5" />} />
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Access Status</div>
        <Badge variant="outline" className={`mt-1 ${access.color}`}>{access.label}</Badge>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">Payment Status</div>
        <Badge variant="outline" className={`mt-1 ${pay.color}`}>{pay.label}</Badge>
      </div>
      <Field label="Limited Access Start" value={dateOnly(s.limited_access_started_at) || "—"} />
      <Field label="Limited Access Expiry" value={dateOnly(s.limited_access_expires_at) || "—"} />
      <Field label="Days Remaining" value={s.access_status === "limited_access_expired" ? "Expired" : `${s.days_remaining || 0}d`} />
      <Field label="Questions Used" value={`${s.questions_used || 0} / ${s.question_limit || 0}`} />
      <Field label="Payment Reference" value={payment?.payment_reference_number || s.latest_payment_reference_number || "—"} />
      <Field label="Registered" value={dateOnly(s.created_at) || "—"} />
      <Field label="Last Login" value={(s.last_login || "").slice(0, 19).replace("T", " ") || "—"} />
    </div>
  );
}

function Field({ label, value, icon }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-medium text-slate-800 dark:text-slate-100 flex items-center gap-1 mt-0.5">{icon}{value}</div>
    </div>
  );
}

function CourseAccessList({ courses, current }) {
  return (
    <Card className="p-4 bg-slate-50 dark:bg-slate-800/50" data-testid="temp-detail-courses">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">All Enrolled Courses</div>
      <ul className="space-y-2 text-sm">
        {courses.map((c) => {
          const badge = c.is_full
            ? { color: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700", label: "Full Access" }
            : (ACCESS_BADGE[c.access_status] || ACCESS_BADGE.pending_approval);
          return (
            <li key={c.course_id} className={`flex items-center justify-between gap-2 ${c.course_id === current ? "font-semibold" : ""}`} data-testid={`temp-detail-course-${c.course_id}`}>
              <span className="min-w-0 truncate">{c.course_name}{c.is_primary && <span className="text-blue-600 text-xs ml-1">· Primary</span>}</span>
              <span className="flex items-center gap-2 shrink-0">
                {!c.is_full && <span className="text-xs text-slate-500">{c.access_status === "limited_access_expired" ? "Expired" : `${c.days_remaining || 0}d left`}</span>}
                <Badge variant="outline" className={badge.color}>{badge.label}</Badge>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
